import { useGetEventPath } from "@shared/modules/events/hooks/useGetEventPath";
import {
  DiscoverySubItemSerializer,
  PublicDiscoveryViewSerializer,
} from "@app-contexts/public/features/discovery/data/types/discovery";
import EventStatusBadge from "@app-contexts/public/features/event-detail/components/EventStatusBadge/EventStatusBadge.web";
import { CityLandingPageEventSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import styled from "@emotion/styled";
import { enums } from "@shared/constants";
import Track from "@shared/modules/analytics/components/Track/Track.web";
import {
  PublicBaseEventSerializer,
  TicketTypeSerializer,
} from "@shared/modules/events/types/public";
import {
  eventHasWaitlistItems,
  getDynamicEventDateRange,
  isMultiDayEvent,
  isSoldOutEvent,
} from "@shared/modules/events/utils/event-utils";
import { getPriceRange } from "@shared/modules/financials/utils/price-utils";
import MembershipRenewalText from "@shared/modules/memberships/components/MembershipRenewalText/MembershipRenewalText.web";
import breakpoints from "@shared/theme/props/breakpoints";
import { getFormattedDate } from "@shared/utils/dates/get-dates/get-dates-timezone";
import { getEventImageSource } from "@shared/utils/images/utils";
import InventoryBadge from "@ui-application/data-display/badges/InventoryBadge/InventoryBadge.web";
import CardImage from "@ui-application/media/CardImage/CardImage.web";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import { useTranslation } from "next-i18next";
import Link from "next/link";

const { xs, sm, lg } = breakpoints;

// TO-DO: Remove usage of styled-components
const Container = styled(Flex, {
  shouldForwardProp: (prop) => prop !== "isLoading",
})<{ isLoading: boolean }>`
  background-color: var(--clr-paint-light-100);
  flex-direction: column;
  cursor: ${({ isLoading }) => (isLoading ? "default" : "pointer")};
  border-radius: 8px;
  border: 1px solid var(--clr-paint-gray-400);
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

const ContentContainer = styled(Flex)`
  flex-grow: 2;
  padding: 15px 10px;
  position: relative;
  height: auto;
  justify-content: space-between;
  flex-direction: column;
`;

const DateStatusWrapper = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  height: 26px;

  @media only screen and (min-width: ${sm}px) {
    height: 20px;
  }

  @media only screen and (min-width: ${lg}px) {
    height: 24px;
  }
`;

const DateText = styled.p`
  font-size: 18.5px;
  line-height: 22px;
  color: var(--clr-paint-primary-800);

  @media only screen and (min-width: ${sm}px) {
    font-size: 12px;
    line-height: 14px;
  }

  @media only screen and (min-width: ${lg}px) {
    font-size: 17px;
    line-height: 21px;
  }
`;

const EventNameText = styled.p`
  margin-top: 16px;
  font-weight: 700;
  font-size: 16px;
  line-height: 19.2px;
  text-overflow: ellipsis;
  color: var(--clr-font-dark);
  -webkit-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  overflow: hidden;
  white-space: normal;

  @media only screen and (min-width: ${sm}px) and (max-width: ${lg}px) {
    font-size: 14px;
    line-height: 16.8px;
  }
`;

const CityText = styled.p`
  margin-top: 8px;
  font-size: 14px;
  line-height: 16.8px;
  color: var(--clr-font-dark);

  @media only screen and (min-width: ${sm}px) and (max-width: ${lg}px) {
    font-size: 12px;
    line-height: 14.4px;
  }
`;

const PriceTimeText = styled.span`
  display: inline-block;
  font-size: 14px;
  line-height: 18px;
  padding-right: 17px;
  border-right: 1px solid var(--clr-font-dark);

  &:last-of-type {
    padding-left: 17px;
    border-right: unset;
    padding-right: unset;
  }

  &:only-of-type {
    padding-left: unset;
  }

  @media only screen and (min-width: 200px) and (max-width: 1360px) {
    padding-right: 7px;

    &:last-of-type {
      padding-left: 7px;
    }

    &:only-of-type {
      padding-left: unset;
    }
  }
`;

const StatusContainer = styled.div`
  position: absolute;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  top: -12px;
`;

export type EventDetailsCardProps = {
  isLoading?: boolean;
  event:
    | PublicBaseEventSerializer
    | CityLandingPageEventSerializer
    | PublicDiscoveryViewSerializer
    | undefined;
  onClick?: () => void;
  tracking_label_suffix?: string;
  hideFavoriteIcon?: boolean;
  allInPricing?: boolean;
};

export const EventDetailsCard: React.FC<EventDetailsCardProps> = ({
  isLoading,
  event,
  onClick,
  tracking_label_suffix,
  hideFavoriteIcon = false,
  allInPricing = false,
}) => {
  const { t } = useTranslation("common");
  const { getEventPath } = useGetEventPath();

  let renewalObj = null;

  // convert any fields to a common name for use inside of display.
  let subItems:
    | TicketTypeSerializer[]
    | DiscoverySubItemSerializer[]
    | undefined;
  let no_sub_items: boolean | undefined = false;

  if (event !== null && event !== undefined) {
    if ("id" in event) {
      subItems = event.ticket_types;
      no_sub_items = event.no_ticket_types;
    } else if ("item_id" in event) {
      subItems = event.sub_items;
      no_sub_items = event.no_sub_items;
      renewalObj = enums.RENEWAL_FREQUENCIES_MAP_BY_LIST.find(
        (renewalItem) => renewalItem.id === event.renewal_frequency
      );
    }
  }

  const prices = no_sub_items
    ? t("Free")
    : getPriceRange({
        items: subItems,
        allInPricing:
          Boolean(event?.venue?.all_in_pricing_is_enabled) || allInPricing,
        venueCurrency: event?.venue?.currency,
      });

  const getEventID = (
    event:
      | PublicBaseEventSerializer
      | CityLandingPageEventSerializer
      | PublicDiscoveryViewSerializer
      | undefined
  ) => {
    if (!event) {
      return undefined;
    }
    if ("id" in event) {
      return event.id;
    }

    return event.item_id;
  };

  const getVenueID = (
    event:
      | PublicBaseEventSerializer
      | CityLandingPageEventSerializer
      | PublicDiscoveryViewSerializer
      | undefined
  ) => {
    if (!event) {
      return undefined;
    }

    if ("venue_id" in event) {
      return event.venue_id;
    }

    if ("venue" in event) {
      return event?.venue?.id;
    }

    return undefined;
  };

  const eventItemType = enums.DISCOVERY_VIEW_TYPE_MAP.event.id;
  const handleClick = onClick
    ? (e: React.MouseEvent) => {
        e.preventDefault();
        onClick();
      }
    : undefined;
  const href = getEventPath(event);

  return (
    <Track.Click
      analytics={{
        label: `event-details-card-${tracking_label_suffix}`,
        event_id: getEventID(event),
        venue_id: getVenueID(event),
        custom_params: {
          is_sponsored: event?.is_sponsored,
          item_type: eventItemType,
          item_id: getEventID(event),
        },
      }}
    >
      <Link
        href={href}
        onClick={handleClick}
        data-testid="event-details-card"
        role="button"
        tabIndex={0}
      >
        <Container tabIndex={0} isLoading={Boolean(isLoading)}>
          <Skeleton isLoaded={!isLoading}>
            <CardImage
              favoriteData={!hideFavoriteIcon ? { event } : undefined}
              src={getEventImageSource(event)}
              alt={t("Event image for {{eventName}}", {
                eventName: event?.name,
              })}
              title={t("Event image for {{eventName}}", {
                eventName: event?.name,
              })}
              ariaLabel={t("Event image for {{eventName}}", {
                eventName: event?.name,
              })}
              height={600}
              width={1200}
              sizes={`(max-width: ${xs}px) ${xs}px, (max-width: ${sm}px) ${sm}px, (min-width: ${sm}px) 400px, 1200px`}
            />
          </Skeleton>
          <ContentContainer>
            <Flex direction="column" width="100%" alignContent="flex-start">
              <DateStatusWrapper>
                <Skeleton isLoaded={!isLoading}>
                  {!!event && event.starts_on && event.ends_on && (
                    <DateText>
                      {getDynamicEventDateRange(event, t("Date & Time TBD"))}
                    </DateText>
                  )}
                  {!!event &&
                    !event.starts_on &&
                    !event.ends_on &&
                    renewalObj && (
                      <DateText>
                        <MembershipRenewalText
                          renewalFrequency={renewalObj?.id}
                          as="span"
                          fontSize="inherit"
                          noOfLines={1}
                        />
                      </DateText>
                    )}
                </Skeleton>
                <StatusContainer>
                  {!!event && (
                    <EventStatusBadge
                      eventStatus={event.status}
                      soldOut={isSoldOutEvent(event)}
                      waitlisted={eventHasWaitlistItems(event)}
                      recurringData={{
                        isRecurring: Boolean(
                          event.is_recurring_parent &&
                            event.child_count &&
                            event.child_count > 1
                        ),
                        isMultiDay: isMultiDayEvent(event),
                      }}
                    />
                  )}
                </StatusContainer>
              </DateStatusWrapper>
              <Skeleton isLoaded={!isLoading}>
                <EventNameText>{event?.name}</EventNameText>
              </Skeleton>
              <Skeleton width="fit-content" isLoaded={!isLoading}>
                <CityText>
                  {event?.location?.city || t("Location Unavailable")}
                </CityText>
              </Skeleton>
            </Flex>
            <Flex direction="column" width="100%" alignContent="flex-end">
              <Box minHeight="element.md" />
              <Skeleton isLoaded={!isLoading}>
                <Box minHeight="element.xl">
                  {/* Display low inventory or remaining tickets badge */}
                  {event && event.venue && (
                    <InventoryBadge eventData={event} venueData={event.venue} />
                  )}
                </Box>
              </Skeleton>
              <Skeleton isLoaded={!isLoading}>
                {prices && <PriceTimeText>{prices}</PriceTimeText>}
                {!!event && event.starts_on && (
                  <PriceTimeText>
                    {event?.starts_on &&
                      getFormattedDate({
                        date: event?.starts_on,
                        timezone: event?.timezone,
                        format: "h:mm A zz",
                      })}
                  </PriceTimeText>
                )}
              </Skeleton>
            </Flex>
          </ContentContainer>
        </Container>
      </Link>
    </Track.Click>
  );
};
